import { Box, Button, Typography, styled } from "@mui/material";

import { themePalette } from "../../../theme";

export const ScheduleProgressTrack = styled(Box)`
  position: relative;
  display: flex;
  padding: 4px 0 8px;
`;

export const ScheduleProgressSteps = styled(Box)`
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  align-items: center;
`;

export const ScheduleProgressConnector = styled(Box)`
  position: absolute;
  top: 18px;
  left: calc(16.666% + 15px);
  right: calc(16.666% + 15px);
  height: 2px;
  background-color: ${themePalette.neutral200};
`;

export const ScheduleProgressStep = styled("span", {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: "complete" | "current" | "upcoming" }>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  font-family: bold;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  color: ${({ status }) =>
    status === "upcoming" ? themePalette.neutral500 : themePalette.greyMedium};

  ${({ theme }) => theme.breakpoints.up("sm")} {
    font-size: 14px;
  }
`;

export const ScheduleProgressMarker = styled("span", {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: "complete" | "current" | "upcoming" }>`
  display: inline-flex;
  width: 28px;
  height: 28px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  border: 2px solid
    ${({ status }) =>
      status === "upcoming" ? themePalette.neutral300 : themePalette.redMedium};
  border-radius: 50%;
  background-color: ${({ status }) =>
    status === "upcoming" ? themePalette.white : themePalette.redMedium};
  color: ${({ status }) =>
    status === "upcoming" ? themePalette.neutral500 : themePalette.white};
  font-family: bold;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;

  & .MuiSvgIcon-root {
    font-size: 18px;
  }
`;

export const DayStrip = styled(Box)`
  display: flex;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow-x: auto;
  padding-bottom: ${({ theme }) => theme.spacing(1)};
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  padding-left: 4px;
  padding-right: 4px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const DayChip = styled(Button, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
  flex: 0 0 auto;
  min-width: 72px;
  padding: 10px 8px;
  border-radius: 12px;
  border: 2px solid
    ${({ selected }) =>
      selected ? themePalette.redMedium : themePalette.neutral200};
  background-color: ${({ selected }) =>
    selected ? "rgba(240, 96, 75, 0.08)" : themePalette.white};
  color: ${themePalette.greyMedium};
  text-transform: none;
  box-shadow: none;

  &:hover {
    background-color: ${({ selected }) =>
      selected ? "rgba(240, 96, 75, 0.12)" : themePalette.neutral50};
    border-color: ${({ selected }) =>
      selected ? themePalette.redMedium : themePalette.neutral300};
    box-shadow: none;
  }

  &.Mui-disabled {
    opacity: 0.55;
    background-color: ${themePalette.neutral50};
    border-color: ${themePalette.neutral200};
  }
`;

export const DayChipWeekday = styled(Typography)`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${themePalette.neutral500};
  line-height: 1.2;
`;

export const DayChipDate = styled(Typography)`
  font-family: bold;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: ${themePalette.greyMedium};
`;

export const DayChipMeta = styled(Typography)`
  font-size: 11px;
  font-weight: 500;
  color: ${themePalette.neutral500};
  line-height: 1.2;
`;

export const TimeSlotGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.up("sm")} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const TimeSlotChip = styled(Button, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
  min-height: 48px;
  padding: 10px 12px;
  border-radius: 30px;
  border: 2px solid
    ${({ selected }) =>
      selected ? themePalette.redMedium : themePalette.neutral200};
  background-color: ${({ selected }) =>
    selected ? themePalette.redMedium : themePalette.white};
  color: ${({ selected }) =>
    selected ? themePalette.white : themePalette.greyMedium};
  font-family: bold;
  font-size: 15px;
  font-weight: 700;
  text-transform: none;
  white-space: nowrap;
  box-shadow: none;

  &:hover {
    background-color: ${({ selected }) =>
      selected ? themePalette.redDark : themePalette.neutral50};
    border-color: ${({ selected }) =>
      selected ? themePalette.redDark : themePalette.neutral300};
    box-shadow: none;
  }
`;

export const AddressSummaryCard = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  border-radius: 8px;
  border: 1px solid ${themePalette.neutral200};
  background-color: ${themePalette.neutral50};
  box-shadow: ${themePalette.boxShadow};
`;

export const AddressSummaryLabel = styled(Typography)`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${themePalette.neutral500};
`;

export const AddressSummaryText = styled(Typography)`
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  color: ${themePalette.greyMedium};
`;

export const GetLabsScheduleContext = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  border: 1px solid ${themePalette.neutral200};
  border-radius: 12px;
  background-color: ${themePalette.neutral50};
`;

export const ReviewSummaryCard = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  border: 2px solid ${themePalette.neutral200};
  border-radius: 12px;
  background-color: ${themePalette.white};
`;

export const ReviewSummaryRow = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: 12px 14px;

  & + & {
    border-top: 1px solid ${themePalette.neutral200};
  }
`;

export const ReviewSummaryContent = styled(Box)`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 2px;
`;

export const SlotContext = styled(Typography)`
  margin: -4px 0 ${({ theme }) => theme.spacing(2)};
  color: ${themePalette.neutral500};
  font-size: 13px;
  line-height: 19px;
`;

export const EditDetailsButton = styled(Button)`
  align-self: flex-start;
  margin-top: 4px;
  padding: 0;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  text-transform: none;
  color: ${themePalette.redMedium};

  &:hover {
    background: transparent;
    text-decoration: underline;
  }
`;

export const StickyBookBar = styled(Box)`
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin: ${({ theme }) => theme.spacing(2)} -16px 0;
  padding: ${({ theme }) => theme.spacing(1.5)} 16px
    ${({ theme }) => theme.spacing(2)};
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    ${themePalette.white} 28%
  );
  display: flex;
  justify-content: center;
`;

export const CompletionResources = styled(Box)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${themePalette.neutral200};
  border-radius: 12px;
  background-color: ${themePalette.neutral50};
  text-align: left;
`;

export const CompletionResourceActions = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.up("sm")} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .MuiButton-root {
    min-height: 44px;
    text-transform: none;
  }
`;
